// HeroStudio.tsx - the interactive editor on the home page hero.
//
// A working miniature of the real Studio shell, not a screenshot: the same
// top bar (project, 2D/3D chip, Scene / Art & Assets / Systems / Setup / Code
// workspaces, Preview, Publish), the same docked viewport toolbar, the same
// left-structure / centre-viewport / right-properties layout, the same
// bottom dock and status bar. Visitors can orbit the model, pick elements,
// recolour them, switch workspaces, read a Logic rule, run a script, open
// Preview and publish, all in pure CSS/DOM with no engine or images loaded.
//
// Everything here is authored demo content for one fictional project. It
// deliberately never imports the engine, so the home page stays light.
// Exports: HeroStudio, HeroStudioMobile, HM_ELEMENTS
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { version as ENGINE_VERSION } from '../../../package.json';
import { Glyph } from '../components/ui-shared';

/* ── demo content ─────────────────────────────────────────────────────── */
export interface HmElement {
  id: string;
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
  colour: string;
}

// hover_skiff: the asset being modelled in the hero graphic. Colours are the
// theme-invariant --hm-* tokens in soulstack.css (this is authored scene
// content, not UI chrome - see the token block's comment there).
export const HM_ELEMENTS: HmElement[] = [
  { id: 'hull', x: 0, y: 30, z: 0, w: 130, h: 24, d: 56, colour: 'var(--hm-hull)' },
  { id: 'canopy', x: 18, y: 54, z: 0, w: 44, h: 16, d: 36, colour: 'var(--hm-canopy)' },
  { id: 'nose', x: 80, y: 32, z: 0, w: 32, h: 16, d: 38, colour: 'var(--hm-nose)' },
  { id: 'tail_fin', x: -58, y: 60, z: 0, w: 14, h: 34, d: 8, colour: 'var(--hm-nose)' },
  { id: 'thruster_l', x: -52, y: 28, z: 42, w: 38, h: 18, d: 16, colour: 'var(--hm-thruster)' },
  { id: 'thruster_r', x: -52, y: 28, z: -42, w: 38, h: 18, d: 16, colour: 'var(--hm-thruster)' },
  { id: 'glow_strip', x: 4, y: 16, z: 0, w: 118, h: 4, d: 48, colour: 'var(--hm-glow-strip)' },
  { id: 'skid_l', x: 8, y: 6, z: 28, w: 64, h: 6, d: 9, colour: 'var(--hm-skid)' },
  { id: 'skid_r', x: 8, y: 6, z: -28, w: 64, h: 6, d: 9, colour: 'var(--hm-skid)' },
];

const HM_SWATCHES = ['--hm-swatch-1', '--hm-swatch-2', '--hm-swatch-3', '--hm-swatch-4', '--hm-swatch-5', '--hm-swatch-6'];

type Workspace = 'scene' | 'art' | 'systems' | 'setup' | 'code';
const WORKSPACES: { id: Workspace; label: string }[] = [
  { id: 'scene', label: 'Scene' },
  { id: 'art', label: 'Art & Assets' },
  { id: 'systems', label: 'Systems' },
  { id: 'setup', label: 'Setup' },
  { id: 'code', label: 'Code' },
];

type Tool = 'select' | 'move' | 'rotate' | 'scale';
const TOOLS: { id: Tool; label: string; key: string }[] = [
  { id: 'select', label: 'Select', key: 'V' },
  { id: 'move', label: 'Move', key: 'G' },
  { id: 'rotate', label: 'Rotate', key: 'R' },
  { id: 'scale', label: 'Scale', key: 'S' },
];

interface SceneNode { name: string; kind: 'group' | 'mesh' | 'light' | 'marker' | 'trigger'; depth: number }
const SCENE_TREE: SceneNode[] = [
  { name: 'objects', kind: 'group', depth: 0 },
  { name: 'hover_skiff', kind: 'mesh', depth: 1 },
  { name: 'crate_stack_a', kind: 'mesh', depth: 1 },
  { name: 'lighting', kind: 'group', depth: 0 },
  { name: 'sun_directional', kind: 'light', depth: 1 },
  { name: 'lamp_bay_a', kind: 'light', depth: 1 },
  { name: 'gameplay', kind: 'group', depth: 0 },
  { name: 'spawn_player', kind: 'marker', depth: 1 },
  { name: 'trigger_skiff_bay', kind: 'trigger', depth: 1 },
];

interface Rule { id: string; name: string; when: string; if: string[]; then: string[] }
const RULES: Rule[] = [
  { id: 'unlock', name: 'Unlock the skiff', when: 'Player enters zone', if: ['Event zone is skiff_bay', 'Item count of keycard is at least 1'], then: ['Announce "Skiff unlocked"', 'Set variable skiff_unlocked to 1', 'Play sound bay_door'] },
  { id: 'score', name: 'Score on delivery', when: 'Player enters zone', if: ['Event zone is drop_point', 'Match variable skiff_unlocked is 1'], then: ['Add 10 to variable score', 'Emit event delivered'] },
  { id: 'win', name: 'Win at 30 points', when: 'Every second', if: ['Match variable score is at least 30'], then: ['Win game'] },
];

const ASSET_TILES: { name: string; kind: string; colour: string }[] = [
  { name: 'hover_skiff', kind: 'Model', colour: 'var(--hm-hull)' },
  { name: 'crate_stack', kind: 'Library', colour: 'var(--hm-swatch-3)' },
  { name: 'bay_door', kind: 'Library', colour: 'var(--hm-thruster)' },
  { name: 'keycard', kind: 'Upload', colour: 'var(--hm-swatch-6)' },
  { name: 'bay_door.ogg', kind: 'Audio', colour: 'var(--hm-swatch-2)' },
  { name: 'pilot', kind: 'Character', colour: 'var(--hm-swatch-4)' },
];

const CODE_LINES: { text: string; tone?: 'k' | 's' | 'f' | 'c' }[] = [
  { text: 'uses entities, variables, logic', tone: 'k' },
  { text: '' },
  { text: '// Spawn a crate near the skiff and count it', tone: 'c' },
  { text: 'let id = effect entities.spawn("crate_stack", 0, 2.0, 0.0, 1.5)' },
  { text: 'effect variables.add("crates", 1.0)' },
  { text: 'effect logic.announce("Crate delivered")', tone: 'f' },
  { text: 'return effect variables.get("crates")' },
];

const CAMERAS = ['3D first person', '3D third person', '2D top down', '2D side view'];

/* ── small building blocks ────────────────────────────────────────────── */
function Label({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span className="hm-label" style={style}>{children}</span>;
}

function PanelHead({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px 6px' }}>
      <Label>{children}</Label>
      {action}
    </div>
  );
}

function Stub({ children, style, accent }: { children: ReactNode; style?: CSSProperties; accent?: boolean }) {
  return (
    <div className="hm-stub" style={{ padding: '5px 8px', fontSize: 9.5, fontWeight: 500, color: accent ? 'var(--ss-accent)' : 'var(--ss-text-2)', ...style }}>
      {children}
    </div>
  );
}

function Seg<T extends string>({ options, value, onChange, label }: { options: readonly T[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 3, background: 'var(--ss-hover)', border: '1px solid var(--ss-border-soft)', borderRadius: 7, padding: 2 }}>
      {options.map((o) => (
        <button key={o} type="button" role="radio" aria-checked={o === value} className={'hm-seg' + (o === value ? ' is-active' : '')} onClick={() => onChange(o)}>
          {o}
        </button>
      ))}
    </div>
  );
}

function Switch({ label, on, onToggle }: { label: string; on: boolean; onToggle: () => void }) {
  return (
    <button type="button" className={'hm-switch' + (on ? ' is-on' : '')} aria-pressed={on} onClick={onToggle}>
      <span className="hm-switch-dot" />
      {label}
    </button>
  );
}

function TreeRow({ node, selected, onClick }: { node: SceneNode; selected: boolean; onClick?: () => void }) {
  const colours: Record<SceneNode['kind'], string> = { group: 'var(--ss-text-3)', mesh: 'var(--ss-accent)', light: 'var(--ss-warning)', marker: 'var(--ss-info)', trigger: 'var(--ss-success)' };
  const isGroup = node.kind === 'group';
  return (
    <button
      type="button"
      className={'hm-row' + (selected ? ' is-selected' : '')}
      onClick={onClick}
      disabled={isGroup}
      style={{ paddingLeft: 8 + node.depth * 12, fontSize: isGroup ? 10 : 10.5, fontWeight: isGroup ? 600 : 450, color: isGroup ? 'var(--ss-text-3)' : undefined, cursor: isGroup ? 'default' : 'pointer' }}
    >
      <span style={{ width: 5, height: 5, borderRadius: isGroup ? 1 : '50%', background: colours[node.kind], flexShrink: 0 }} />
      {node.name}
    </button>
  );
}

/* ── the 3D stage ─────────────────────────────────────────────────────── */
const HM_HANDLE_CORNERS: Array<[string, string]> = [['0%', '0%'], ['100%', '0%'], ['0%', '100%'], ['100%', '100%']];

function Face({ style, colour, shade, sel, handles, onPick }: { style: CSSProperties; colour: string; shade: number; sel: boolean; handles: boolean; onPick?: () => void }) {
  return (
    <div
      className={'hm-face' + (sel && handles ? ' hm-select-glow' : '')}
      onClick={onPick}
      style={{ position: 'absolute', left: '50%', top: '50%', background: colour, outline: sel ? '1px solid var(--ss-accent)' : 'none', outlineOffset: sel && !handles ? 1 : 0, boxShadow: 'inset 0 0 0 200px rgba(0,0,0,' + shade + ')', cursor: onPick ? 'pointer' : 'default', ...style }}
    >
      {sel && handles && HM_HANDLE_CORNERS.map(([cx, cy]) => (
        <span key={cx + cy} className="hm-handle" style={{ left: cx, top: cy, transform: 'translate(-50%, -50%)' }} />
      ))}
    </div>
  );
}

function Box({ el, sel, handles, onPick }: { el: HmElement; sel: boolean; handles: boolean; onPick?: () => void }) {
  const { x, y, z, w, h, d, colour } = el;
  const face = (fw: number, fh: number, t: string): CSSProperties => ({ width: fw, height: fh, marginLeft: -fw / 2, marginTop: -fh / 2, transform: t });
  return (
    <div style={{ position: 'absolute', left: '50%', top: '50%', transformStyle: 'preserve-3d', transform: 'translate3d(' + x + 'px, ' + (-y - h / 2) + 'px, ' + z + 'px)' }}>
      <Face colour={colour} shade={0.06} sel={sel} handles={handles} onPick={onPick} style={face(w, h, 'translateZ(' + d / 2 + 'px)')} />
      <Face colour={colour} shade={0.34} sel={sel} handles={handles} onPick={onPick} style={face(w, h, 'rotateY(180deg) translateZ(' + d / 2 + 'px)')} />
      <Face colour={colour} shade={0.2} sel={sel} handles={handles} onPick={onPick} style={face(d, h, 'rotateY(90deg) translateZ(' + w / 2 + 'px)')} />
      <Face colour={colour} shade={0.26} sel={sel} handles={handles} onPick={onPick} style={face(d, h, 'rotateY(-90deg) translateZ(' + w / 2 + 'px)')} />
      <Face colour={colour} shade={0} sel={sel} handles={handles} onPick={onPick} style={face(w, d, 'rotateX(90deg) translateZ(' + h / 2 + 'px)')} />
    </div>
  );
}

const BASE_TILT = -24;
const TOP_DOWN_TILT = -78;
const ORBIT_DEG_PER_SEC = 12;
const DRAG_DEG_PER_PX = 0.45;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface StageProps {
  elements: HmElement[];
  selectedId: string | null;
  /** 'element' picks a single element (Model Studio); 'object' picks the
   * whole model (Scene); 'none' disables picking (Preview). */
  pick: 'element' | 'object' | 'none';
  onPick: (id: string | null) => void;
  grid: boolean;
  topDown: boolean;
  scale?: number;
  height: number | string;
  children?: ReactNode;
}

/**
 * The viewport. Auto-orbits slowly (never under prefers-reduced-motion),
 * pauses while the visitor drags, and follows a real mouse with a small
 * tilt. Orbit and tilt are written straight to the DOM from one animation
 * frame loop so a 60fps orbit never re-renders the React tree.
 */
function Stage({ elements, selectedId, pick, onPick, grid, topDown, scale = 1, height, children }: StageProps) {
  const orbitRef = useRef<HTMLDivElement>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const angle = useRef(38);
  const tilt = useRef({ x: 0, y: 0 });
  const drag = useRef<{ id: number; lastX: number; moved: number } | null>(null);
  const moved = useRef(0);

  useEffect(() => {
    let raf = 0;
    let last: number | null = null;
    const still = prefersReducedMotion();
    const frame = (t: number) => {
      const dt = last === null ? 0 : (t - last) / 1000;
      last = t;
      if (!still && !drag.current && !topDown) angle.current = (angle.current + dt * ORBIT_DEG_PER_SEC) % 360;
      if (orbitRef.current) orbitRef.current.style.transform = 'rotateY(' + angle.current + 'deg)';
      if (tiltRef.current) {
        const base = topDown ? TOP_DOWN_TILT : BASE_TILT;
        tiltRef.current.style.transform = 'translateY(' + (topDown ? 10 : 54) * scale + 'px) rotateX(' + (base + tilt.current.x) + 'deg) rotateY(' + tilt.current.y + 'deg) scale(' + scale + ')';
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [topDown, scale]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    drag.current = { id: e.pointerId, lastX: e.clientX, moved: 0 };
    moved.current = 0;
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current && drag.current.id === e.pointerId) {
      const dx = e.clientX - drag.current.lastX;
      drag.current.lastX = e.clientX;
      drag.current.moved += Math.abs(dx);
      moved.current = drag.current.moved;
      angle.current = (angle.current + dx * DRAG_DEG_PER_PX + 360) % 360;
      return;
    }
    if (e.pointerType !== 'mouse' || prefersReducedMotion()) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width - 0.5;
    const ny = (e.clientY - rect.top) / rect.height - 0.5;
    tilt.current = { x: -ny * 6, y: nx * 6 };
  };
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current && drag.current.id === e.pointerId) {
      drag.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };
  const onPointerLeave = () => { tilt.current = { x: 0, y: 0 }; };
  // A click that followed a real drag is an orbit, not a pick.
  const pickAfterDrag = (id: string | null) => { if (moved.current < 4) onPick(id); };

  return (
    <div
      className="hm-stage"
      style={{ position: 'relative', overflow: 'hidden', height, touchAction: 'pan-y', userSelect: 'none', cursor: 'grab', background: 'radial-gradient(ellipse 80% 70% at 50% 38%, var(--ss-surface), var(--ss-bg-2) 85%)' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={onPointerLeave}
      onClick={() => pickAfterDrag(null)}
    >
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 42% 26% at 50% 62%, var(--ss-accent-soft), transparent 70%)' }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: 900 }}>
        <div ref={tiltRef} style={{ position: 'relative', width: 340, height: 300, transformStyle: 'preserve-3d', transition: 'transform 220ms ease-out' }}>
          <div ref={orbitRef} style={{ position: 'absolute', inset: 0, transformStyle: 'preserve-3d' }}>
            {grid && (
              <div style={{ position: 'absolute', left: '50%', top: '50%', width: 300, height: 300, marginLeft: -150, marginTop: -150, transform: 'rotateX(90deg)', backgroundImage: 'linear-gradient(var(--ss-accent-soft) 1px, transparent 1px), linear-gradient(90deg, var(--ss-accent-soft) 1px, transparent 1px)', backgroundSize: '25px 25px', border: '1px solid var(--ss-border-soft)', maskImage: 'radial-gradient(circle, #000 40%, transparent 78%)', WebkitMaskImage: 'radial-gradient(circle, #000 40%, transparent 78%)' }} />
            )}
            <div style={{ position: 'absolute', left: '50%', top: '50%', width: 200, height: 110, marginLeft: -100, marginTop: -55, transform: 'rotateX(90deg) translateZ(-1px)', background: 'radial-gradient(ellipse, var(--ss-accent-dim), transparent 65%)', filter: 'blur(6px)' }} />
            {elements.map((el) => (
              <Box
                key={el.id}
                el={el}
                sel={pick === 'object' ? selectedId === 'hover_skiff' : selectedId === el.id}
                handles={pick === 'element'}
                onPick={pick === 'none' ? undefined : () => pickAfterDrag(pick === 'object' ? 'hover_skiff' : el.id)}
              />
            ))}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

/* ── viewport toolbar (docked above the viewport, as in the editor) ────── */
function ViewportBar({ tool, setTool, snap, setSnap, grid, setGrid, fly, setFly, dim }: {
  tool: Tool; setTool: (t: Tool) => void;
  snap: boolean; setSnap: (v: boolean) => void;
  grid: boolean; setGrid: (v: boolean) => void;
  fly: boolean; setFly: (v: boolean) => void;
  dim: '2D' | '3D';
}) {
  return (
    <div className="hm-vbar">
      <div style={{ display: 'flex', gap: 2 }}>
        {TOOLS.map((t) => (
          <button key={t.id} type="button" className={'hm-tool' + (tool === t.id ? ' is-active' : '')} title={t.label + ' · ' + t.key} aria-pressed={tool === t.id} onClick={() => setTool(t.id)}>
            <ToolIcon kind={t.id} />
            <span className="hm-tool-key">{t.key}</span>
          </button>
        ))}
      </div>
      <span className="hm-vsep" />
      <Switch label="SNAP" on={snap} onToggle={() => setSnap(!snap)} />
      {dim === '3D' && <Switch label="GRID" on={grid} onToggle={() => setGrid(!grid)} />}
      {dim === '3D' && <Switch label="FLY" on={fly} onToggle={() => setFly(!fly)} />}
      <span className="hm-vsep" />
      <span className="hm-vchip">Floor 0</span>
      <span className="hm-vchip" style={{ marginLeft: 'auto', color: 'var(--ss-accent)', borderColor: 'var(--ss-accent-border)', background: 'var(--ss-accent-soft)' }}>{dim}</span>
    </div>
  );
}

function ToolIcon({ kind }: { kind: Tool }) {
  const s = { stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };
  if (kind === 'select') return <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2l10 6-4.5 1.5L7 14z" {...s} /></svg>;
  if (kind === 'move') return <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2v12M2 8h12M8 2l-2 2M8 2l2 2M8 14l-2-2M8 14l2-2M2 8l2-2M2 8l2 2M14 8l-2-2M14 8l-2 2" {...s} /></svg>;
  if (kind === 'rotate') return <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><path d="M13 8a5 5 0 1 1-2-4M13 2v3h-3" {...s} /></svg>;
  return <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13V8M3 13h5M13 3v5M13 3H8M3 13l10-10" {...s} /></svg>;
}

/* ── workspace panes ──────────────────────────────────────────────────── */
interface StudioState {
  elements: HmElement[];
  setElements: (next: HmElement[]) => void;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  grid: boolean;
  topDown: boolean;
}

function ScenePane({ s, viewport }: { s: StudioState; viewport: ReactNode }) {
  const [treeSel, setTreeSel] = useState<string>('hover_skiff');
  const [emissive, setEmissive] = useState(35);
  const [roughness, setRoughness] = useState(60);
  const selectedNode = SCENE_TREE.find((n) => n.name === treeSel) ?? null;
  return (
    <Fragment>
      <div className="hm-side">
        <PanelHead action={<span style={{ fontSize: 9, fontWeight: 600, color: 'var(--ss-accent)' }}>+ Add</span>}>Scene</PanelHead>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {SCENE_TREE.map((n) => (
            <TreeRow key={n.name} node={n} selected={treeSel === n.name} onClick={() => { setTreeSel(n.name); s.setSelectedId(n.name === 'hover_skiff' ? 'hover_skiff' : null); }} />
          ))}
        </div>
        <Label style={{ marginTop: 'auto', padding: '0 4px', color: 'var(--ss-text-3)', letterSpacing: '0.08em', textTransform: 'none', fontWeight: 500 }}>Click the skiff, drag to orbit</Label>
      </div>
      {viewport}
      <div className="hm-side hm-side--right">
        <PanelHead>Inspector</PanelHead>
        {selectedNode ? (
          <Fragment>
            <Stub style={{ color: 'var(--ss-text)' }}>{selectedNode.name}</Stub>
            <Label>Position</Label>
            <div style={{ display: 'flex', gap: 4 }}><Stub style={{ flex: 1 }}>4.5</Stub><Stub style={{ flex: 1 }}>0.0</Stub><Stub style={{ flex: 1 }}>-2.0</Stub></div>
            <Label>Rotation</Label>
            <div style={{ display: 'flex', gap: 4 }}><Stub style={{ flex: 1 }}>0°</Stub><Stub style={{ flex: 1 }}>38°</Stub><Stub style={{ flex: 1 }}>0°</Stub></div>
            {selectedNode.kind === 'mesh' && (
              <Fragment>
                <Label>Material</Label>
                <Stub accent>metal_worn</Stub>
                <Slider label="Emissive" value={emissive} onChange={setEmissive} />
                <Slider label="Roughness" value={roughness} onChange={setRoughness} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: 'var(--ss-text-2)' }}><span>Collidable</span><span style={{ color: 'var(--ss-success)' }}>Yes</span></div>
              </Fragment>
            )}
            {selectedNode.kind === 'light' && (
              <Fragment>
                <Label>Kind</Label>
                <Stub accent>Point</Stub>
                <Slider label="Intensity" value={74} />
                <Slider label="Range" value={38} />
              </Fragment>
            )}
            {selectedNode.kind === 'trigger' && (
              <Fragment>
                <Label>Kind</Label>
                <Stub accent>Event</Stub>
                <Label>Zone id</Label>
                <Stub>skiff_bay</Stub>
                <Label style={{ color: 'var(--ss-text-3)', textTransform: 'none', letterSpacing: '0.04em', fontWeight: 500 }}>Fires "Unlock the skiff" in Systems</Label>
              </Fragment>
            )}
            {selectedNode.kind === 'marker' && (
              <Fragment>
                <Label>Kind</Label>
                <Stub accent>Spawn</Stub>
                <Label>Facing</Label>
                <Stub>180°</Stub>
              </Fragment>
            )}
          </Fragment>
        ) : (
          <Stub>Nothing selected</Stub>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
          <Stub style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>Duplicate</Stub>
          <Stub style={{ flex: 1, textAlign: 'center', fontWeight: 600, color: 'var(--ss-danger)' }}>Delete</Stub>
        </div>
      </div>
    </Fragment>
  );
}

function Slider({ label, value, onChange }: { label: string; value: number; onChange?: (v: number) => void }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: 'var(--ss-text-2)' }}>
        <span>{label}</span>
        <span style={{ fontWeight: 500, fontSize: 9, color: 'var(--ss-text-3)' }}>{value}%</span>
      </span>
      <input type="range" className="hm-range" min={0} max={100} value={value} aria-label={label} readOnly={!onChange} onChange={(e) => onChange?.(Number(e.target.value))} />
    </label>
  );
}

function ArtPane({ s, viewport }: { s: StudioState; viewport: ReactNode }) {
  const [tab, setTab] = useState<'Model Studio' | 'Assets'>('Model Studio');
  const sel = s.elements.find((e) => e.id === s.selectedId) ?? null;
  const [removed, setRemoved] = useState<HmElement | null>(null);

  const recolour = (token: string) => {
    if (!sel) return;
    s.setElements(s.elements.map((e) => (e.id === sel.id ? { ...e, colour: 'var(' + token + ')' } : e)));
  };
  const duplicate = () => {
    if (!sel) return;
    const copy: HmElement = { ...sel, id: sel.id + '_copy', y: sel.y + sel.h + 6 };
    if (s.elements.some((e) => e.id === copy.id)) return;
    s.setElements([...s.elements, copy]);
    s.setSelectedId(copy.id);
  };
  const remove = () => {
    if (!sel) return;
    setRemoved(sel);
    s.setElements(s.elements.filter((e) => e.id !== sel.id));
    s.setSelectedId(null);
  };
  const undo = () => {
    if (!removed) return;
    s.setElements([...s.elements, removed]);
    s.setSelectedId(removed.id);
    setRemoved(null);
  };

  return (
    <Fragment>
      <div className="hm-side">
        <div style={{ display: 'flex', gap: 4, padding: '0 0 8px' }}>
          {(['Model Studio', 'Assets'] as const).map((t) => (
            <button key={t} type="button" className={'hm-seg' + (tab === t ? ' is-active' : '')} onClick={() => setTab(t)} style={{ flex: 1 }}>{t}</button>
          ))}
        </div>
        {tab === 'Model Studio' ? (
          <Fragment>
            <PanelHead>Elements · hover_skiff</PanelHead>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {s.elements.map((e) => (
                <button key={e.id} type="button" className={'hm-row' + (s.selectedId === e.id ? ' is-selected' : '')} onClick={() => s.setSelectedId(e.id)}>
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: e.colour, border: '1px solid var(--ss-border-soft)', flexShrink: 0 }} />
                  {e.id}
                </button>
              ))}
            </div>
            {removed && (
              <button type="button" className="hm-row" onClick={undo} style={{ color: 'var(--ss-accent)', fontWeight: 600 }}>Undo delete {removed.id}</button>
            )}
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {['box', 'sphere', 'cylinder', 'wedge'].map((p) => <Stub key={p} style={{ flex: 1, textAlign: 'center', fontSize: 8.5 }}>+ {p}</Stub>)}
              </div>
              <div style={{ background: 'var(--ss-accent)', color: 'var(--ss-accent-ink)', borderRadius: 8, padding: '6px 0', fontSize: 10, fontWeight: 600, textAlign: 'center' }}>Save to assets</div>
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <PanelHead action={<span style={{ fontSize: 9, fontWeight: 600, color: 'var(--ss-accent)' }}>Upload .glb</span>}>Project assets</PanelHead>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {ASSET_TILES.map((a) => (
                <div key={a.name} className="hm-stub" style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ height: 30, borderRadius: 5, background: 'linear-gradient(140deg, ' + a.colour + ', var(--ss-bg-2))' }} />
                  <span style={{ fontSize: 8.5, fontWeight: 600, color: 'var(--ss-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                  <span style={{ fontSize: 7.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>{a.kind}</span>
                </div>
              ))}
            </div>
            <Label style={{ marginTop: 'auto', textTransform: 'none', letterSpacing: '0.04em', fontWeight: 500, color: 'var(--ss-text-3)' }}>Plus a shared library of thousands of ready models</Label>
          </Fragment>
        )}
      </div>
      {viewport}
      <div className="hm-side hm-side--right">
        <PanelHead>Element</PanelHead>
        {sel ? (
          <Fragment>
            <Stub style={{ color: 'var(--ss-text)' }}>{sel.id}</Stub>
            <Label>Position</Label>
            <div style={{ display: 'flex', gap: 4 }}><Stub style={{ flex: 1 }}>{sel.x}</Stub><Stub style={{ flex: 1 }}>{sel.y}</Stub><Stub style={{ flex: 1 }}>{sel.z}</Stub></div>
            <Label>Size</Label>
            <div style={{ display: 'flex', gap: 4 }}><Stub style={{ flex: 1 }}>{sel.w}</Stub><Stub style={{ flex: 1 }}>{sel.h}</Stub><Stub style={{ flex: 1 }}>{sel.d}</Stub></div>
            <Label>Colour · click a swatch</Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5 }}>
              {HM_SWATCHES.map((c) => {
                const active = sel.colour === 'var(' + c + ')';
                return (
                  <button key={c} type="button" aria-label={'Colour ' + c.replace('--hm-swatch-', '')} aria-pressed={active} onClick={() => recolour(c)} className="hm-swatch" style={{ background: 'var(' + c + ')', borderColor: active ? 'var(--ss-accent)' : 'var(--ss-border-soft)' }} />
                );
              })}
            </div>
            <Label>Boolean</Label>
            <div style={{ display: 'flex', gap: 4 }}>{['Add', 'Subtract', 'Intersect'].map((b, i) => <Stub key={b} accent={i === 0} style={{ flex: 1, textAlign: 'center', fontSize: 8.5 }}>{b}</Stub>)}</div>
          </Fragment>
        ) : (
          <Stub>Pick an element in the list or the viewport</Stub>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
          <button type="button" className="hm-btn" disabled={!sel} onClick={duplicate}>Duplicate</button>
          <button type="button" className="hm-btn hm-btn--danger" disabled={!sel} onClick={remove}>Delete</button>
        </div>
      </div>
    </Fragment>
  );
}

function RuleChip({ children, tone }: { children: ReactNode; tone: 'when' | 'if' | 'then' }) {
  const colour = tone === 'when' ? 'var(--ss-info)' : tone === 'if' ? 'var(--ss-warning)' : 'var(--ss-success)';
  return <span className="hm-stub" style={{ padding: '4px 8px', fontSize: 9.5, color: 'var(--ss-text)', borderLeft: '2px solid ' + colour }}>{children}</span>;
}

function SystemsPane() {
  const [ruleId, setRuleId] = useState<string>('unlock');
  const [feed, setFeed] = useState<string[]>([]);
  const rule = RULES.find((r) => r.id === ruleId) ?? RULES[0]!;
  const test = () => {
    const lines = [rule.when + (rule.if[0] ? ' · ' + rule.if[0] : ''), ...rule.then.map((t) => '→ ' + t)];
    setFeed((f) => [...lines, ...f].slice(0, 6));
  };
  return (
    <Fragment>
      <div className="hm-side">
        <PanelHead action={<span style={{ fontSize: 9, fontWeight: 600, color: 'var(--ss-accent)' }}>+ Rule</span>}>Logic · rules</PanelHead>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {RULES.map((r) => (
            <button key={r.id} type="button" className={'hm-row' + (ruleId === r.id ? ' is-selected' : '')} onClick={() => setRuleId(r.id)}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ss-success)', flexShrink: 0 }} />
              {r.name}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {['HUD', 'Audio', 'FX', 'Game Data', 'Cutscenes', 'Intro Sequences'].map((t) => (
            <span key={t} className="hm-row" style={{ color: 'var(--ss-text-3)', fontSize: 9.5, cursor: 'default' }}>{t}</span>
          ))}
        </div>
      </div>
      <div className="hm-centre" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{rule.name}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9, color: 'var(--ss-success)', fontWeight: 600, letterSpacing: '0.1em' }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ss-success)' }} />ENABLED</span>
        </div>
        <div className="hm-stub" style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Label style={{ color: 'var(--ss-info)' }}>When</Label>
          <RuleChip tone="when">{rule.when}</RuleChip>
        </div>
        <div className="hm-stub" style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Label style={{ color: 'var(--ss-warning)' }}>If</Label>
          {rule.if.map((c) => <RuleChip key={c} tone="if">{c}</RuleChip>)}
          <span style={{ fontSize: 9, color: 'var(--ss-text-3)' }}>+ Condition</span>
        </div>
        <div className="hm-stub" style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Label style={{ color: 'var(--ss-success)' }}>Then</Label>
          {rule.then.map((a) => <RuleChip key={a} tone="then">{a}</RuleChip>)}
          <span style={{ fontSize: 9, color: 'var(--ss-text-3)' }}>+ Action</span>
        </div>
        <span style={{ fontSize: 9.5, color: 'var(--ss-text-3)', marginTop: 'auto' }}>No code. Rules run wherever your game runs, and sync to every player.</span>
      </div>
      <div className="hm-side hm-side--right">
        <PanelHead action={<button type="button" className="hm-btn hm-btn--primary" onClick={test}>Test rule</button>}>Output</PanelHead>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontFamily: 'var(--ss-mono)', fontSize: 9 }}>
          {feed.length === 0 ? (
            <span style={{ color: 'var(--ss-text-3)', fontFamily: 'var(--ss-sans)', fontSize: 9.5 }}>Press Test rule to fire it once.</span>
          ) : feed.map((line, i) => (
            <span key={i} style={{ color: i === 0 ? 'var(--ss-text)' : 'var(--ss-text-2)', opacity: 1 - i * 0.12, padding: '3px 6px', background: line.startsWith('→') ? 'var(--ss-success-soft)' : 'var(--ss-hover)', borderRadius: 5 }}>{line}</span>
          ))}
        </div>
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Label>Reference</Label>
          {[['Events', '8'], ['Facts', '10'], ['Actions', '11']].map(([k, n]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: 'var(--ss-text-2)' }}><span>{k}</span><span style={{ color: 'var(--ss-text-3)' }}>{n}</span></div>
          ))}
        </div>
      </div>
    </Fragment>
  );
}

function SetupPane({ gameType, setGameType, camera, setCamera }: { gameType: string; setGameType: (v: 'Solo' | 'Co-op' | 'Versus') => void; camera: string; setCamera: (v: string) => void }) {
  const [gravity, setGravity] = useState('9.8');
  const multiplayer = gameType !== 'Solo';
  return (
    <div className="hm-centre" style={{ gridColumn: '1 / -1', padding: 14, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, overflow: 'hidden' }}>
      <div className="hm-stub" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Label>Rules</Label>
        <span style={{ fontSize: 9.5, color: 'var(--ss-text-2)' }}>Game type</span>
        <Seg label="Game type" options={['Solo', 'Co-op', 'Versus'] as const} value={gameType as 'Solo' | 'Co-op' | 'Versus'} onChange={setGameType} />
        {multiplayer ? (
          <Fragment>
            <span style={{ fontSize: 9.5, color: 'var(--ss-text-2)' }}>Team format</span>
            <div style={{ display: 'flex', gap: 4 }}>{(gameType === 'Versus' ? ['1v1', '2v2', '5v5', 'FFA'] : ['2', '3', '4']).map((t, i) => <Stub key={t} accent={i === (gameType === 'Versus' ? 2 : 2)} style={{ flex: 1, textAlign: 'center' }}>{t}</Stub>)}</div>
            <span style={{ fontSize: 9.5, color: 'var(--ss-text-2)' }}>Win condition</span>
            <Stub accent>Score target</Stub>
            <span style={{ fontSize: 9, color: 'var(--ss-text-3)' }}>Players see each other and share Logic state automatically.</span>
          </Fragment>
        ) : (
          <Fragment>
            <span style={{ fontSize: 9.5, color: 'var(--ss-text-2)' }}>Respawn</span>
            <Stub accent>At checkpoint</Stub>
            <span style={{ fontSize: 9, color: 'var(--ss-text-3)' }}>Switch to Co-op or Versus to open a player pool.</span>
          </Fragment>
        )}
      </div>
      <div className="hm-stub" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Label>Camera & projection</Label>
        {CAMERAS.map((c) => (
          <button key={c} type="button" className={'hm-row' + (camera === c ? ' is-selected' : '')} aria-pressed={camera === c} onClick={() => setCamera(c)} style={{ border: '1px solid ' + (camera === c ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'), padding: '7px 9px' }}>
            <span style={{ width: 6, height: 6, borderRadius: c.startsWith('2D') ? 1 : '50%', background: camera === c ? 'var(--ss-accent)' : 'var(--ss-text-3)', flexShrink: 0 }} />
            {c}
          </button>
        ))}
        <span style={{ fontSize: 9, color: 'var(--ss-text-3)' }}>Switch any time. The whole studio follows.</span>
      </div>
      <div className="hm-stub" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Label>World</Label>
        <span style={{ fontSize: 9.5, color: 'var(--ss-text-2)' }}>Gravity</span>
        <input className="hm-input" value={gravity} onChange={(e) => setGravity(e.target.value)} aria-label="Gravity" inputMode="decimal" />
        <span style={{ fontSize: 9.5, color: 'var(--ss-text-2)' }}>Input map</span>
        {[['Move', 'WASD / Stick'], ['Interact', 'E'], ['Primary', 'Mouse 1 / R2'], ['Menu', 'Esc']].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: 'var(--ss-text-2)' }}><span>{k}</span><span className="ss-kbd" style={{ fontSize: 8.5, padding: '1px 6px' }}>{v}</span></div>
        ))}
        <span style={{ fontSize: 9.5, color: 'var(--ss-text-2)', marginTop: 4 }}>Collision layers</span>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{['World', 'Players', 'Props', 'Triggers'].map((l) => <Stub key={l} style={{ fontSize: 8.5 }}>{l}</Stub>)}</div>
      </div>
    </div>
  );
}

function CodePane() {
  const [output, setOutput] = useState<string[]>([]);
  const [runs, setRuns] = useState(0);
  const run = () => {
    const n = runs + 1;
    setRuns(n);
    setOutput([`> run skiff_bay.hws`, `spawned crate_stack as crate_${n}`, `announce: Crate delivered`, `= ${n}.0`]);
  };
  return (
    <Fragment>
      <div className="hm-side">
        <PanelHead action={<span style={{ fontSize: 9, fontWeight: 600, color: 'var(--ss-accent)' }}>+ New</span>}>Scripts</PanelHead>
        {[['on_start.hws', true], ['skiff_bay.hws', false]].map(([name, auto]) => (
          <span key={String(name)} className={'hm-row' + (name === 'skiff_bay.hws' ? ' is-selected' : '')} style={{ cursor: 'default' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: auto ? 'var(--ss-success)' : 'var(--ss-border-strong)', flexShrink: 0 }} />
            {name}
          </span>
        ))}
        <span style={{ fontSize: 9, color: 'var(--ss-text-3)', padding: '6px 4px' }}>A green dot runs on game start.</span>
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Label>Capabilities</Label>
          {['entities', 'variables', 'audio', 'logic'].map((c) => (
            <div key={c} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: 'var(--ss-text-2)' }}><span>{c}</span><span style={{ color: 'var(--ss-success)' }}>granted</span></div>
          ))}
        </div>
      </div>
      <div className="hm-centre" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--ss-border-soft)' }}>
          <span style={{ fontSize: 10.5, fontWeight: 600 }}>skiff_bay.hws <span style={{ color: 'var(--ss-text-3)', fontWeight: 500 }}>· HollowScript</span></span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: 'var(--ss-text-3)' }}>Saved</span>
            <button type="button" className="hm-btn hm-btn--primary" onClick={run} title="Run · Ctrl/Cmd + Enter">Run</button>
          </div>
        </div>
        <div className="doc-code" style={{ flex: 1, margin: 0, borderRadius: 0, border: 'none', padding: '12px 14px' }}>
          <pre style={{ fontSize: 10.5, lineHeight: 1.75 }}>
            {CODE_LINES.map((l, i) => (
              <span key={i} style={{ display: 'block' }}>
                <span style={{ display: 'inline-block', width: 18, color: 'var(--ss-text-3)', opacity: 0.6 }}>{i + 1}</span>
                <span className={l.tone ? 'tok-' + l.tone : undefined}>{l.text}</span>
              </span>
            ))}
          </pre>
        </div>
      </div>
      <div className="hm-side hm-side--right">
        <PanelHead action={<button type="button" className="hm-btn" onClick={() => setOutput([])} disabled={output.length === 0}>Clear</button>}>Run output</PanelHead>
        <div style={{ fontFamily: 'var(--ss-mono)', fontSize: 9.5, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {output.length === 0 ? <span style={{ fontFamily: 'var(--ss-sans)', color: 'var(--ss-text-3)' }}>Press Run to execute the script against the live scene.</span> : output.map((o, i) => <span key={i} style={{ color: i === 0 ? 'var(--ss-text-3)' : 'var(--ss-text)' }}>{o}</span>)}
        </div>
        <span style={{ marginTop: 'auto', fontSize: 9, color: 'var(--ss-text-3)' }}>Scripts only get the capabilities you grant in Setup. Nothing else.</span>
      </div>
    </Fragment>
  );
}

/* ── preview overlay (the HUD players see) ────────────────────────────── */
function PreviewHud({ camera, onExit }: { camera: string; onExit: () => void }) {
  const is2d = camera.startsWith('2D');
  return (
    <Fragment>
      <span className="hm-chip" style={{ position: 'absolute', top: 10, left: 10, color: 'var(--ss-accent)' }}>PREVIEW · LOCAL</span>
      <button type="button" className="hm-btn" onClick={onExit} style={{ position: 'absolute', top: 8, right: 10, background: 'var(--ss-glass)' }}>Exit preview · Esc</button>
      <div style={{ position: 'absolute', left: 12, bottom: 12, display: 'flex', flexDirection: 'column', gap: 4, width: 120 }}>
        <span style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.12em', color: 'var(--ss-text-3)' }}>HEALTH</span>
        <span style={{ height: 6, borderRadius: 999, background: 'var(--ss-border-soft)', overflow: 'hidden' }}><span className="hm-hud-health" style={{ display: 'block', height: '100%', background: 'var(--ss-success)' }} /></span>
      </div>
      <span className="hm-chip" style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', fontFamily: 'var(--ss-mono)' }}>02:41</span>
      <span style={{ position: 'absolute', right: 12, bottom: 12, fontFamily: 'var(--ss-mono)', fontSize: 14, fontWeight: 600, color: 'var(--ss-text)' }}>12 <span style={{ fontSize: 9, color: 'var(--ss-text-3)' }}>/ 30</span></span>
      <span className="hm-chip" style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', color: 'var(--ss-text-3)' }}>
        {is2d ? 'WASD OR ARROW KEYS TO MOVE' : 'CLICK TO CAPTURE MOUSE · ESC TO RELEASE'}
      </span>
      <span style={{ position: 'absolute', left: '50%', top: '50%', width: 10, height: 10, marginLeft: -5, marginTop: -5, border: '1px solid var(--ss-text-2)', borderRadius: '50%', opacity: 0.6, pointerEvents: 'none' }} />
    </Fragment>
  );
}

/* ── publish popover ──────────────────────────────────────────────────── */
function PublishPopover({ version, onPublish, onClose }: { version: number; onPublish: () => void; onClose: () => void }) {
  const [vis, setVis] = useState<'Public' | 'Unlisted' | 'Off'>('Public');
  const [live, setLive] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const publish = () => {
    setBusy(true);
    window.setTimeout(() => { setBusy(false); setLive(version + 1); onPublish(); }, 700);
  };
  return (
    <div className="hm-popover" role="dialog" aria-label="Publish" onClick={(e) => e.stopPropagation()}>
      {live !== null ? (
        <Fragment>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
            <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--ss-accent-soft)', border: '1px solid var(--ss-accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="var(--ss-accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>v{live} is live</span>
            <span style={{ fontSize: 9.5, color: 'var(--ss-text-3)' }}>Hosted, at its own address, installable on any device.</span>
          </div>
          <Stub style={{ fontFamily: 'var(--ss-mono)', color: 'var(--ss-text)' }}>sstk.studio/play/skiff-run</Stub>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {['Play in browser', 'Install as app', 'Every version kept'].map((t) => <span key={t} className="hm-chip" style={{ fontSize: 8.5 }}>{t}</span>)}
          </div>
          <button type="button" className="hm-btn hm-btn--primary" style={{ width: '100%', justifyContent: 'center' }} onClick={onClose}>Done</button>
        </Fragment>
      ) : (
        <Fragment>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Publish</span>
            <span style={{ fontSize: 9.5, color: 'var(--ss-text-3)' }}>Snapshot the project and update the live game.</span>
          </div>
          <Label>Visibility</Label>
          <Seg label="Visibility" options={['Public', 'Unlisted', 'Off'] as const} value={vis} onChange={setVis} />
          <Label>Play URL</Label>
          <Stub style={{ fontFamily: 'var(--ss-mono)', color: version === 0 ? 'var(--ss-text-3)' : 'var(--ss-text)' }}>{version === 0 ? 'Claimed on first publish' : 'sstk.studio/play/skiff-run'}</Stub>
          <button type="button" className="hm-btn hm-btn--primary" style={{ width: '100%', justifyContent: 'center' }} onClick={publish} disabled={busy}>
            {busy ? 'Publishing…' : 'Publish v' + (version + 1)}
          </button>
        </Fragment>
      )}
    </div>
  );
}

/* ── the full window ──────────────────────────────────────────────────── */
export function HeroStudio() {
  const [workspace, setWorkspace] = useState<Workspace>('scene');
  const [elements, setElements] = useState<HmElement[]>(HM_ELEMENTS);
  const [selectedId, setSelectedId] = useState<string | null>('hover_skiff');
  const [tool, setTool] = useState<Tool>('select');
  const [snap, setSnap] = useState(true);
  const [grid, setGrid] = useState(true);
  const [fly, setFly] = useState(false);
  const [preview, setPreview] = useState(false);
  const [pubOpen, setPubOpen] = useState(false);
  const [version, setVersion] = useState(12);
  const [gameType, setGameType] = useState<'Solo' | 'Co-op' | 'Versus'>('Co-op');
  const [camera, setCamera] = useState<string>('3D first person');
  const dim: '2D' | '3D' = camera.startsWith('2D') ? '2D' : '3D';
  const topDown = camera === '2D top down';

  // Escape leaves Preview, exactly as it does in the editor.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreview(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  // Selection follows the workspace: Scene selects the whole object, Model
  // Studio selects one element of it.
  const switchWorkspace = (w: Workspace) => {
    setWorkspace(w);
    setPubOpen(false);
    if (w === 'scene') setSelectedId('hover_skiff');
    else if (w === 'art') setSelectedId(elements.some((e) => e.id === 'canopy') ? 'canopy' : (elements[0]?.id ?? null));
  };

  const state: StudioState = { elements, setElements, selectedId, setSelectedId, grid, topDown };
  const stage = (
    <div className="hm-centre" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <ViewportBar tool={tool} setTool={setTool} snap={snap} setSnap={setSnap} grid={grid} setGrid={setGrid} fly={fly} setFly={setFly} dim={dim} />
      <Stage elements={elements} selectedId={selectedId} pick={workspace === 'art' ? 'element' : 'object'} onPick={setSelectedId} grid={grid && dim === '3D'} topDown={topDown} height="100%">
        <span className="hm-chip" style={{ position: 'absolute', top: 10, left: 10 }}>{workspace === 'art' ? 'hover_skiff.model · ' + elements.length + ' elements' : 'Skiff Run · Floor 0'}</span>
        {selectedId && <span className="hm-chip" style={{ position: 'absolute', top: 10, right: 10, color: 'var(--ss-accent)' }}>Selected · {selectedId}</span>}
        <span className="hm-chip" style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', color: 'var(--ss-text-3)' }}>
          {fly ? 'FLY · WASD + mouse' : dim === '2D' ? 'Drag to pan · Scroll zooms' : 'Drag to orbit · F focus · ? shortcuts'}
        </span>
      </Stage>
    </div>
  );

  return (
    <div className="hm-window" role="region" aria-label="Interactive preview of the Soulstack Studio editor" onClick={() => pubOpen && setPubOpen(false)}>
      {/* top bar */}
      <div className="hm-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Glyph size={17} />
          <span style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap' }}>Skiff Run</span>
          <span className="hm-label" style={{ border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '2px 7px', whiteSpace: 'nowrap' }}>{version > 0 ? 'Live v' + version : 'Not published'}</span>
          <span className="hm-label" style={{ color: 'var(--ss-accent)', border: '1px solid var(--ss-accent-border)', background: 'var(--ss-accent-soft)', borderRadius: 999, padding: '2px 7px' }}>{dim}</span>
        </div>
        <div className="hm-tabs" role="tablist" aria-label="Workspace">
          {WORKSPACES.map((w) => (
            <button key={w.id} type="button" role="tab" aria-selected={workspace === w.id && !preview} className={'hm-tab' + (workspace === w.id && !preview ? ' is-active' : '')} onClick={() => { setPreview(false); switchWorkspace(w.id); }}>
              {w.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
          <span className="hm-label" style={{ letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Saved</span>
          <button type="button" className={'hm-btn' + (preview ? ' is-active' : '')} aria-pressed={preview} onClick={() => { setPubOpen(false); setPreview(!preview); }}>
            <svg width="7" height="8" viewBox="0 0 10 11" fill="none" aria-hidden="true"><path d="M1.5 1.3v8.4l7.2-4.2-7.2-4.2z" fill="currentColor" /></svg>
            Preview
          </button>
          <button type="button" className="hm-btn hm-btn--primary" aria-expanded={pubOpen} onClick={(e) => { e.stopPropagation(); setPubOpen(!pubOpen); }}>Publish</button>
          {pubOpen && <PublishPopover version={version} onPublish={() => setVersion((v) => v + 1)} onClose={() => setPubOpen(false)} />}
        </div>
      </div>
      {/* body */}
      <div className="hm-body" style={{ gridTemplateColumns: preview || workspace === 'setup' ? '1fr' : '196px 1fr 208px' }}>
        {preview ? (
          <Stage elements={elements} selectedId={null} pick="none" onPick={() => {}} grid={false} topDown={topDown} scale={1.15} height="100%">
            <PreviewHud camera={camera} onExit={() => setPreview(false)} />
          </Stage>
        ) : workspace === 'scene' ? (
          <ScenePane s={state} viewport={stage} />
        ) : workspace === 'art' ? (
          <ArtPane s={state} viewport={stage} />
        ) : workspace === 'systems' ? (
          <SystemsPane />
        ) : workspace === 'setup' ? (
          <SetupPane gameType={gameType} setGameType={setGameType} camera={camera} setCamera={setCamera} />
        ) : (
          <CodePane />
        )}
      </div>
      {/* dock */}
      <div className="hm-dock">
        {['Assets', 'Prefabs', 'Console', 'Output', 'Profiler'].map((t, i) => (
          <span key={t} className={'hm-dock-tab' + (i === 0 ? ' is-active' : '')}>{t}</span>
        ))}
      </div>
      {/* status bar */}
      <div className="hm-status">
        <span>Engine {ENGINE_VERSION}</span>
        <span>Grid 0.5m · Snap {snap ? 'on' : 'off'}</span>
        <span>{gameType === 'Solo' ? 'Solo' : gameType + ' · 4 players'} · 60 fps</span>
      </div>
    </div>
  );
}

/* ── phone version ────────────────────────────────────────────────────── */
/**
 * Single-pane version for phone widths: the same live viewport (drag to
 * orbit, tap an element chip to select it, tap a swatch to recolour it)
 * with the side panels folded into a chip row, so nothing is lost but the
 * fixed-pixel panels that could never fit.
 */
export function HeroStudioMobile() {
  const [elements, setElements] = useState<HmElement[]>(HM_ELEMENTS);
  const [selectedId, setSelectedId] = useState<string | null>('canopy');
  const sel = elements.find((e) => e.id === selectedId) ?? null;
  return (
    <div className="hm-window" role="region" aria-label="Interactive preview of the Soulstack Studio editor">
      <div className="hm-topbar" style={{ height: 40, padding: '0 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Glyph size={16} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>Skiff Run</span>
        </div>
        <span className="hm-label" style={{ color: 'var(--ss-accent)', border: '1px solid var(--ss-accent-border)', background: 'var(--ss-accent-soft)', borderRadius: 999, padding: '2px 7px' }}>Model Studio · 3D</span>
      </div>
      <Stage elements={elements} selectedId={selectedId} pick="element" onPick={setSelectedId} grid topDown={false} scale={0.8} height={250}>
        <span className="hm-chip" style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', color: 'var(--ss-text-3)' }}>Drag to orbit · tap to select</span>
      </Stage>
      <div style={{ display: 'flex', gap: 5, padding: '10px 10px 0', overflowX: 'auto' }}>
        {elements.map((e) => (
          <button key={e.id} type="button" className={'hm-seg' + (selectedId === e.id ? ' is-active' : '')} onClick={() => setSelectedId(e.id)} style={{ whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: 2, background: e.colour, display: 'inline-block', marginRight: 5, border: '1px solid var(--ss-border-soft)' }} />{e.id}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 10 }}>
        <Label>Colour</Label>
        <div style={{ display: 'flex', gap: 5, flex: 1 }}>
          {HM_SWATCHES.map((c) => {
            const active = sel?.colour === 'var(' + c + ')';
            return <button key={c} type="button" className="hm-swatch" aria-label={'Colour ' + c.replace('--hm-swatch-', '')} aria-pressed={active} disabled={!sel} onClick={() => sel && setElements(elements.map((e) => (e.id === sel.id ? { ...e, colour: 'var(' + c + ')' } : e)))} style={{ background: 'var(' + c + ')', borderColor: active ? 'var(--ss-accent)' : 'var(--ss-border-soft)', width: 22, height: 22 }} />;
          })}
        </div>
      </div>
    </div>
  );
}
